import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateTeaGradeRequest,
    ListTeaGradesParams,
    PatchTeaGradeRequest,
} from '@/lib/api/tea-grade-api'
import { teaGradeApi } from '@/lib/api/tea-grade-api'
import { m } from '@/lib/paraglide/messages'

export const teaGradeKeys = {
    all: ['tea-grades'] as const,
    list: (params: ListTeaGradesParams | undefined) =>
        [...teaGradeKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) => [...teaGradeKeys.all, 'detail', id] as const,
}

export function useTeaGradesList(params?: ListTeaGradesParams) {
    return useQuery({
        queryKey: teaGradeKeys.list(params),
        queryFn: async () => {
            const res = await teaGradeApi.list(params)
            return res
        },
    })
}

export function useTeaGrade(id: string | undefined) {
    return useQuery({
        queryKey: id
            ? teaGradeKeys.detail(id)
            : ['tea-grades', 'detail', 'none'],
        enabled: !!id,
        queryFn: async () => {
            const res = await teaGradeApi.get(id ?? '')
            return res.data
        },
    })
}

export function useCreateTeaGrade() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateTeaGradeRequest) => teaGradeApi.create(data),
        onSuccess: () => {
            toast.success(m.tea_grade_create_success())
            void qc.invalidateQueries({ queryKey: teaGradeKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_create_error())
        },
    })
}

export function usePatchTeaGrade() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchTeaGradeRequest
        }) => teaGradeApi.patch(id, data),
        onSuccess: () => {
            toast.success(m.tea_grade_update_success())
            void qc.invalidateQueries({ queryKey: teaGradeKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_update_error())
        },
    })
}

export function useDeleteTeaGrade() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => teaGradeApi.remove(id),
        onSuccess: () => {
            toast.success(m.tea_grade_delete_success())
            void qc.invalidateQueries({ queryKey: teaGradeKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_delete_error())
        },
    })
}
